/**
 * PipelineMonitor: the Rigs console's Monitor tab (admins only), event
 * pipeline design 13, section 8. One page per job: every capture's stage
 * (captured, uploaded, queued, processing, ready for review, approved or
 * photos only, emailed, page opened, download link issued; discarded, left
 * out, deleted), the counts per stage, the computers (the paired NYC Bridge
 * machines and the capture PC uploader) and the alerts, in plain words.
 *
 * Read only. Every action lives on another tab, and the monitor only says
 * which (onOpenTab switches the console to it; Machines is Traffic
 * Control's page). Data: one admin-only callable, getPipelineMonitor
 * (functions/src/services/pipelineMonitor.ts), page by page, every 30 seconds
 * while the tab is on screen. It carries no guest address and only masked
 * names (initials) with registration numbers. The stage and alert rules are
 * monitorModel.js.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./rigv2-review.css";
import "./rigv2-monitor.css";
import { rigv2 } from "./rigv2Callables.js";
import {
  STAGES, STAGE_BY_ID, TAB_LABEL, alertsOf, computerLine, countsOf, detailOf, filterRows, processingOn, stageOf, uploaderLine,
} from "./monitorModel.js";

export const POLL_MS = 30_000;
export const PAGE_SIZE = 250;
// At most this many pages a refresh (2000 captures): a job past it shows its first 2000.
export const MAX_PAGES = 8;
export const SHOW_STEP = 60;

const STAGE_CHIP = {
  capturing: "working", captured: "stopped", uploading: "working", uploaded: "stopped", queued: "paused", processing: "working",
  failed: "error", review: "needs", approved: "live", photos_only: "live", emailed: "live", opened: "live", downloaded: "live",
  not_captured: "stopped", discarded: "stopped", left_out: "stopped", deleted: "stopped",
};

function StatusChip({ kind, children }) {
  return <span className={`rv-status ${kind}`}><i aria-hidden="true" />{children}</span>;
}

// A link to the tab where an action lives (Machines is another app: words only).
function TabLink({ tab, onOpenTab }) {
  if (tab === "machines" || !onOpenTab) return <span className="pm-where">{TAB_LABEL[tab] ?? tab}</span>;
  return <button type="button" className="rv-link pm-tablink" onClick={() => onOpenTab(tab)}>Open {TAB_LABEL[tab] ?? tab}</button>;
}

/** The members of a capture as the row shows them: "#060 N. V. +1". */
export function peopleLabel(members) {
  const list = members ?? [];
  if (!list.length) return "No one on record";
  const first = [list[0].no ? `#${String(list[0].no).replace(/^#/, "")}` : "", list[0].name].filter(Boolean).join(" ") || "Guest";
  return list.length > 1 ? `${first} +${list.length - 1}` : first;
}

/** The view (pure props: exported for the test). */
export function PipelineMonitorView({
  rows, header, now, truncated = false, filter = "all", query = "", shown = SHOW_STEP, onFilter, onQuery, onMore, onOpenTab,
}) {
  const counts = useMemo(() => countsOf(rows), [rows]);
  const alerts = useMemo(() => alertsOf(rows, header, now), [rows, header, now]);
  const alertSns = useMemo(() => new Set(alerts.flatMap((a) => a.sns)), [alerts]);
  const list = useMemo(() => filterRows(rows, { stage: filter, query, alertSns }), [rows, filter, query, alertSns]);
  const visible = list.slice(0, shown);
  const sw = header?.switches ?? null;
  const computers = header?.computers ?? [];
  const up = uploaderLine(header?.uploader ?? null, now);

  return (
    <>
      <section className="pm-alerts" aria-label="Alerts">
        {alerts.length === 0 ? (
          <div className="pm-allclear" role="status">Nothing needs you right now.</div>
        ) : alerts.map((a) => (
          <div key={a.id} className={`pm-alert ${a.level}`} role={a.level === "error" ? "alert" : "status"}>
            <span className="pm-alert-text">{a.text}</span>
            <TabLink tab={a.tab} onOpenTab={onOpenTab} />
          </div>
        ))}
      </section>

      {sw && (
        <p className="rv-muted pm-switches">
          Processing {processingOn(header) ? "is on for this job" : sw.listed ? "is switched on for this job, but the queue is paused" : "is off for this job"}.
          {" "}Review {sw.reviewOn ? "on" : "off"}; auto-send {sw.autoSendOn ? "on" : "off"}{sw.fieldsUnsafe ? " (review held on while processing is on, whatever the job's switch says)" : ""}. (Settings tab, Automation.)
        </p>
      )}

      <div className="pm-counts" role="group" aria-label="Captures by stage">
        <button type="button" className={`pm-count ${filter === "all" ? "on" : ""}`} aria-pressed={filter === "all"} onClick={() => onFilter("all")}>
          <span className="v">{(rows ?? []).length}</span><span className="l">All</span>
        </button>
        {alertSns.size > 0 && (
          <button type="button" className={`pm-count warn ${filter === "alerts" ? "on" : ""}`} aria-pressed={filter === "alerts"} onClick={() => onFilter("alerts")}>
            <span className="v">{alertSns.size}</span><span className="l">With an alert</span>
          </button>
        )}
        {STAGES.filter((s) => counts[s.id] > 0 || ["captured", "uploaded", "queued", "processing", "review", "approved", "emailed"].includes(s.id)).map((s) => (
          <button key={s.id} type="button" className={`pm-count ${filter === s.id ? "on" : ""}`} aria-pressed={filter === s.id} onClick={() => onFilter(s.id)}>
            <span className="v">{counts[s.id]}</span><span className="l">{s.label}</span>
          </button>
        ))}
      </div>
      {truncated && <p className="rv-footnote">This job has more captures than the monitor reads at once: these are its first {PAGE_SIZE * MAX_PAGES}.</p>}

      <section className="pm-computers" aria-label="Computers">
        <h3 className="pm-h">Computers</h3>
        {computers.length === 0 && !up && <p className="rv-muted">No computer paired and no uploader report for this job yet.</p>}
        <ul className="pm-clist">
          {up && (
            <li className="pm-computer">
              <div className="pm-crow">
                <span className="rv-name">Capture PC uploader</span>
                <StatusChip kind={up.online ? "live" : "stopped"}>{up.online ? "Online" : "Offline"}</StatusChip>
              </div>
              <span className="rv-meta">{[up.doing, up.queue, up.seen, up.version].filter(Boolean).join(" · ")}</span>
            </li>
          )}
          {computers.map((c) => {
            const l = computerLine(c, now);
            return (
              <li key={c.uid} className="pm-computer">
                <div className="pm-crow">
                  <span className="rv-name">{c.label}</span>
                  <span className="rv-sn">{c.kind === "processing" ? "processing computer" : "NYC Bridge"}</span>
                  <StatusChip kind={l.online ? (c.holding ? "working" : "live") : "stopped"}>{l.online ? (c.holding ? "Working" : "Online") : "Offline"}</StatusChip>
                </div>
                <span className="rv-meta">{[l.doing, l.rest, l.seen, l.version].filter(Boolean).join(" · ")}</span>
              </li>
            );
          })}
        </ul>
        <p className="rv-footnote">Pair, revoke or update a computer: <TabLink tab="machines" />. Pause or switch processing: <TabLink tab="processing" onOpenTab={onOpenTab} /></p>
      </section>

      <section className="pm-captures" aria-label="Captures">
        <div className="pm-caphead">
          <h3 className="pm-h">Captures{filter !== "all" ? `: ${filter === "alerts" ? "with an alert" : STAGE_BY_ID[filter]?.label ?? filter}` : ""}</h3>
          <input className="rv-input pm-search" type="search" placeholder="SN, number or initials" aria-label="Find a capture"
            value={query} onChange={(e) => onQuery(e.target.value)} />
        </div>
        {(rows ?? []).length === 0 ? (
          <div className="rv-empty"><p className="rv-empty-title">No captures yet</p><p>Each capture shows here from the moment it is fired.</p></div>
        ) : visible.length === 0 ? (
          <div className="rigv2-empty">None here.</div>
        ) : (
          <ul className="pm-rows">
            {visible.map((r) => {
              const stage = stageOf(r);
              const meta = STAGE_BY_ID[stage];
              const warn = alertSns.has(r.sn);
              return (
                <li key={r.sn} className={`pm-row ${warn ? "warn" : ""}`}>
                  <div className="pm-row-main">
                    <span className="rv-name">{peopleLabel(r.members)}</span>
                    <span className="rv-sn mono" title={r.sn}>{r.sn}</span>
                    <StatusChip kind={STAGE_CHIP[stage] ?? "stopped"}>{meta?.label ?? stage}</StatusChip>
                  </div>
                  <span className={`rv-line ${warn ? "warn" : ""}`}>{detailOf(r, header, now)}</span>
                  <span className="pm-row-foot"><TabLink tab={meta?.tab ?? "captures"} onOpenTab={onOpenTab} /></span>
                </li>
              );
            })}
          </ul>
        )}
        {list.length > visible.length && (
          <p className="rv-footnote">
            Showing {visible.length} of {list.length}.
            <button type="button" className="rv-link" onClick={onMore}>Show {Math.min(SHOW_STEP, list.length - visible.length)} more</button>
          </p>
        )}
      </section>
    </>
  );
}

/**
 * Every page of the job, one after another (at most MAX_PAGES): { rows,
 * header, now, truncated }, or null when keepGoing() said stop between pages
 * (the tab went away or the browser tab was hidden: no more reads for it).
 * `call` is getPipelineMonitor (a test passes its own).
 */
export async function loadAllPages(jobId, call = rigv2.getPipelineMonitor, keepGoing = () => true) {
  const rows = [];
  let header = null;
  let now = Date.now();
  let after;
  for (let i = 0; i < MAX_PAGES; i++) {
    if (i > 0 && !keepGoing()) return null;
    const page = await call({ jobId, limit: PAGE_SIZE, ...(after ? { after } : {}) });
    if (i === 0) { header = page.header ?? null; now = page.now ?? now; }
    rows.push(...(page.captures ?? []));
    if (!page.nextAfter) return { rows, header, now, truncated: false };
    after = page.nextAfter;
  }
  return { rows, header, now, truncated: true };
}

const pageHidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";

/**
 * The refresh loop, one read at a time (pure of React: exported for the
 * test). start() reads now unless a read is already under way (a slow read is
 * never overtaken and thrown away: the next tick waits for it); a read
 * stops between pages once stop() was called or the page is hidden. onData
 * and onError get only the answers of a read that was not stopped.
 */
export function makeRefresher({ load, onStart, onData, onError, hidden = pageHidden }) {
  let running = null;
  let stopped = false;
  const start = () => {
    if (stopped) return null;
    if (running) return running.done;
    const run = { cancelled: false };
    running = run;
    onStart?.();
    const keepGoing = () => !run.cancelled && !stopped && !hidden();
    const done = (async () => {
      try {
        const d = await load(keepGoing);
        if (d && keepGoing()) onData(d);
      } catch (e) {
        if (!run.cancelled && !stopped) onError(e);
      } finally {
        if (running === run) running = null;
      }
    })();
    run.done = done;
    return done;
  };
  return {
    start,
    busy: () => Boolean(running),
    stop: () => { stopped = true; if (running) running.cancelled = true; },
  };
}

export default function PipelineMonitor({ jobId, onOpenTab }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [shown, setShown] = useState(SHOW_STEP);
  const refresher = useRef(null);

  // Now, then every 30 seconds while the tab is on screen (never while the
  // browser tab is hidden), and on coming back; one read at a time.
  useEffect(() => {
    if (!jobId) return undefined;
    setData(null);
    setLoadError("");
    const r = makeRefresher({
      load: (keepGoing) => loadAllPages(jobId, rigv2.getPipelineMonitor, keepGoing),
      onStart: () => setLoading(true),
      onData: (d) => { setData(d); setLoadError(""); },
      onError: (e) => setLoadError(`Could not load the monitor (${e?.message || "no answer"}). It tries again in 30 seconds.`),
    });
    refresher.current = r;
    const tick = () => {
      if (pageHidden()) return;
      const p = r.start();
      if (p) p.finally(() => { if (!r.busy()) setLoading(false); });
    };
    tick();
    const t = setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      r.stop();
      clearInterval(t);
      document.removeEventListener("visibilitychange", tick);
      setLoading(false);
    };
  }, [jobId]);
  const refresh = useCallback(() => {
    const r = refresher.current;
    const p = r?.start();
    if (p) p.finally(() => { if (!r.busy()) setLoading(false); });
  }, []);

  return (
    <div className="rigv2-review rigv2-monitor">
      <div className="rv-head">
        <div className="rv-head-left">
          <span className="rv-eyebrow">Monitor</span>
          <span className="rv-sub">
            Every capture of this job from the rig to the guest, the computers, and what needs you.
            {data?.header?.name ? ` ${data.header.name}.` : ""} Reads only: each line says where its action is.
          </span>
        </div>
        <button type="button" className="rigv2-btn small" disabled={loading} onClick={refresh}>{loading ? "Loading…" : "Refresh"}</button>
      </div>
      {loadError && <div className="rv-banner error" role="status">{loadError}</div>}
      {!data ? (
        loadError ? null : <div className="rigv2-empty">Loading the monitor…</div>
      ) : (
        <PipelineMonitorView
          rows={data.rows} header={data.header} now={data.now} truncated={data.truncated}
          filter={filter} query={query} shown={shown}
          onFilter={(f) => { setFilter(f); setShown(SHOW_STEP); }}
          onQuery={(q) => { setQuery(q); setShown(SHOW_STEP); }}
          onMore={() => setShown((n) => n + SHOW_STEP)}
          onOpenTab={onOpenTab}
        />
      )}
    </div>
  );
}
